import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useUser } from "../../Context/UserContext";
import { DataGrid, GridToolbar, useGridApiRef } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Link, useNavigate, useParams } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

//  Utility function to calculate required width for a single line
const calculateColumnWidth = (headerName, minWidth = 120) => {
  const calculatedWidth = Math.ceil(headerName.length * 8.5) + 50;
  return Math.max(minWidth, calculatedWidth);
};

//  Color definitions matching your MUI theme
const COLORS = {
  summaryGroup: "FFD9EAD3", // Green background
  saplingsGroup: "FFF1C27D", // Orange background
  districtGroup: "FFD9EAD3", // Green background
  headerBackground: "FFE8EAF6", // Light purple for column headers
  headerFont: "00000000", // Black font
  whiteFont: "FFFFFFFF", // White font
  border: "FFBDBDBD", // Light gray border
};

const DistrictReport = () => {
  const [reportData, setReportData] = useState([]);
  const [allSpeciesList, setAllSpeciesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const { token } = useUser();
  const apiRef = useGridApiRef();
  const [searchText, setSearchText] = useState("");
  const { id } = useParams();

  // useEffect(() => {
  //   if (!token) return;
  //   setLoading(true);
  //   axios
  //     .get("/api/center/district-summary/", {
  //       headers: { Authorization: `Bearer ${token}` },
  //     })
  //     .then((res) => {
  //       setReportData(res.data.districts || []);
  //       setAllSpeciesList(res.data.allSpecies || []);
  //     })
  //     .catch((err) => console.error("Error:", err))
  //     .finally(() => setLoading(false));
  // }, [token]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);

    //  UPDATED: Append department_id to the API call if it exists
    const url = id
      ? `/api/center/district-summary/?department_id=${id}`
      : `/api/center/district-summary/`;

    axios
      .get(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setReportData(res.data.districts || []);
        setAllSpeciesList(res.data.allSpecies || []);
      })
      .catch((err) => console.error("Error:", err))
      .finally(() => setLoading(false));
  }, [token, id]);

  const { columns, columnGroupingModel } = useMemo(() => {
    const baseColumns = [
      {
        field: "district_name",
        headerName: "District",
        width: 150,
        pinning: "left",
        renderCell: (params) => (
          <Link
            to={`/district/${params.row.id}/BlockReport`}
            style={{
              textDecoration: "none",
              color: "#1976d2",
              fontWeight: "500",
            }}
          >
            {params.value}
          </Link>
        ),
      },
      {
        field: "production_center_count",
        headerName: "Prod. Centers",
        width: 150,
        pinning: "left",
      },
    ];

    const summaryColumns = [
      {
        field: "species_count",
        headerName: "Species Count",
        width: 150,
        pinning: "right",
      },
      {
        field: "total_target",
        headerName: "Target",
        width: 120,
        pinning: "right",
      },
      {
        field: "total_stock_saplings",
        headerName: "Stock",
        width: 120,
        pinning: "right",
      },
      {
        field: "total_selled",
        headerName: "Total Sold",
        width: 140,
        pinning: "right",
      },
      {
        field: "total_selled_price",
        headerName: "Total Sold Price",
        width: 170,
        pinning: "right",
      },
    ];

    const speciesColumns = allSpeciesList.map((s) => {
      const name = s.name || s.species_name || "";
      return {
        field: `species_${s.id}`,
        headerName: name,
        width: calculateColumnWidth(name, 120),
        type: "number",
        valueGetter: (value, row) =>
          row?.saplingLookup?.[String(s.id)]?.qty || 0,
      };
    });

    const groups = [
      {
        groupId: "Summary",
        children: summaryColumns.map((c) => ({ field: c.field })),
        headerClassName: "summary-group-header",
      },
      {
        groupId: "Saplings",
        children: speciesColumns.map((s) => ({ field: s.field })),
        headerClassName: "saplings-group-header",
      },
    ];

    return {
      columns: [...baseColumns, ...summaryColumns, ...speciesColumns],
      columnGroupingModel: groups,
    };
  }, [allSpeciesList]);

  //  Excel Export Function with Styling
  const exportToExcel = useCallback(async () => {
    setExporting(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("District Report", {
        properties: { tabColor: { argb: "FF1976D2" } },
      });

      // Define column structures for export
      const baseColumns = [
        { header: "District", field: "district_name" },
        { header: "Prod. Centers", field: "production_center_count" },
      ];

      const summaryColumns = [
        { header: "Species Count", field: "species_count" },
        { header: "Target", field: "total_target" },
        { header: "Stock", field: "total_stock_saplings" },
        { header: "Total Sold", field: "total_selled" },
        { header: "Total Sold Price", field: "total_selled_price" },
      ];

      const speciesColumns = allSpeciesList.map((s) => ({
        header: s.name || s.species_name || "",
        field: `species_${s.id}`,
        getValue: (row) => row?.saplingLookup?.[String(s.id)]?.qty || 0,
      }));

      const allColumns = [...baseColumns, ...summaryColumns, ...speciesColumns];

      // Calculate column positions
      const baseColCount = baseColumns.length; // 2
      const summaryColCount = summaryColumns.length; // 5
      const speciesColCount = speciesColumns.length;

      // ============================================
      // ROW 1: Group Headers (Merged Cells)
      // ============================================

      // District Info Group (columns 1-2)
      worksheet.mergeCells(1, 1, 1, baseColCount);
      const districtGroupCell = worksheet.getCell(1, 1);
      districtGroupCell.value = "District Info";
      districtGroupCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: COLORS.districtGroup },
      };
      districtGroupCell.font = {
        bold: true,
        size: 12,
        color: { argb: COLORS.headerFont },
      };
      districtGroupCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      districtGroupCell.border = {
        top: { style: "thin", color: { argb: COLORS.border } },
        bottom: { style: "thin", color: { argb: COLORS.border } },
        left: { style: "thin", color: { argb: COLORS.border } },
        right: { style: "thin", color: { argb: COLORS.border } },
      };

      // Apply border to merged cells in District group
      for (let col = 2; col <= baseColCount; col++) {
        const cell = worksheet.getCell(1, col);
        cell.border = {
          top: { style: "thin", color: { argb: COLORS.border } },
          bottom: { style: "thin", color: { argb: COLORS.border } },
          right: { style: "thin", color: { argb: COLORS.border } },
        };
      }

      // Summary Group (columns 3-7)
      const summaryStart = baseColCount + 1;
      const summaryEnd = baseColCount + summaryColCount;
      worksheet.mergeCells(1, summaryStart, 1, summaryEnd);
      const summaryGroupCell = worksheet.getCell(1, summaryStart);
      summaryGroupCell.value = "Summary";
      summaryGroupCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: COLORS.summaryGroup },
      };
      summaryGroupCell.font = {
        bold: true,
        size: 12,
        color: { argb: COLORS.headerFont },
      };
      summaryGroupCell.alignment = { horizontal: "center", vertical: "middle" };
      summaryGroupCell.border = {
        top: { style: "thin", color: { argb: COLORS.border } },
        bottom: { style: "thin", color: { argb: COLORS.border } },
        left: { style: "thin", color: { argb: COLORS.border } },
        right: { style: "thin", color: { argb: COLORS.border } },
      };

      // Apply border to merged cells in Summary group
      for (let col = summaryStart + 1; col <= summaryEnd; col++) {
        const cell = worksheet.getCell(1, col);
        cell.border = {
          top: { style: "thin", color: { argb: COLORS.border } },
          bottom: { style: "thin", color: { argb: COLORS.border } },
          right: { style: "thin", color: { argb: COLORS.border } },
        };
      }

      // Saplings Group (remaining columns)
      const saplingsStart = summaryEnd + 1;
      const saplingsEnd = summaryEnd + speciesColCount;
      worksheet.mergeCells(1, saplingsStart, 1, saplingsEnd);
      const saplingsGroupCell = worksheet.getCell(1, saplingsStart);
      saplingsGroupCell.value = "Saplings";
      saplingsGroupCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: COLORS.saplingsGroup },
      };
      saplingsGroupCell.font = {
        bold: true,
        size: 12,
        color: { argb: COLORS.headerFont },
      };
      saplingsGroupCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      saplingsGroupCell.border = {
        top: { style: "thin", color: { argb: COLORS.border } },
        bottom: { style: "thin", color: { argb: COLORS.border } },
        left: { style: "thin", color: { argb: COLORS.border } },
        right: { style: "thin", color: { argb: COLORS.border } },
      };

      // Apply border to merged cells in Saplings group
      for (let col = saplingsStart + 1; col <= saplingsEnd; col++) {
        const cell = worksheet.getCell(1, col);
        cell.border = {
          top: { style: "thin", color: { argb: COLORS.border } },
          bottom: { style: "thin", color: { argb: COLORS.border } },
          right: { style: "thin", color: { argb: COLORS.border } },
        };
      }

      // ============================================
      // ROW 2: Column Headers
      // ============================================
      const headerRow = worksheet.addRow(allColumns.map((c) => c.header));
      headerRow.height = 25;

      headerRow.eachCell((cell, colNumber) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: COLORS.headerBackground },
        };
        cell.font = {
          bold: true,
          size: 10,
          color: { argb: COLORS.headerFont },
        };
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: false,
        };
        cell.border = {
          top: { style: "thin", color: { argb: COLORS.border } },
          bottom: { style: "medium", color: { argb: "FF666666" } },
          left: { style: "thin", color: { argb: COLORS.border } },
          right: { style: "thin", color: { argb: COLORS.border } },
        };
      });

      // ============================================
      // DATA ROWS
      // ============================================
      reportData.forEach((row, rowIndex) => {
        const rowData = allColumns.map((col) => {
          if (col.getValue) return col.getValue(row);
          return row[col.field];
        });

        const dataRow = worksheet.addRow(rowData);

        // Alternate row colors
        const isEven = rowIndex % 2 === 0;
        const rowFill = isEven
          ? { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } }
          : {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF5F5F5" },
            };

        dataRow.eachCell((cell, colNumber) => {
          cell.fill = rowFill;
          cell.border = {
            top: { style: "thin", color: { argb: COLORS.border } },
            bottom: { style: "thin", color: { argb: COLORS.border } },
            left: { style: "thin", color: { argb: COLORS.border } },
            right: { style: "thin", color: { argb: COLORS.border } },
          };

          // Align numbers to right, text to left
          if (typeof cell.value === "number") {
            cell.alignment = { horizontal: "right", vertical: "middle" };
            cell.numFmt = "#,##0"; // Number formatting
          } else {
            cell.alignment = { horizontal: "left", vertical: "middle" };
          }

          // Special formatting for Total Sold Price (last summary column)
          if (colNumber === summaryEnd) {
            cell.numFmt = "#,##0.00"; // Currency formatting
          }
        });
      });

      // ============================================
      // COLUMN WIDTHS
      // ============================================
      worksheet.columns.forEach((column, index) => {
        const header = allColumns[index].header;
        // Calculate width: max of header length or sample data
        let maxWidth = header.length + 4;

        // Sample first 10 rows for width calculation
        const sampleRows = reportData.slice(0, 10);
        sampleRows.forEach((row) => {
          const col = allColumns[index];
          let value;
          if (col.getValue) {
            value = col.getValue(row);
          } else {
            value = row[col.field];
          }
          if (value !== null && value !== undefined) {
            const strValue = String(value);
            maxWidth = Math.max(maxWidth, strValue.length + 2);
          }
        });

        column.width = Math.min(maxWidth * 1.2, 30); // Cap at 30 characters
      });

      // ============================================
      // FREEZE PANES (Freeze first 2 columns and header rows)
      // ============================================
      worksheet.views = [
        { state: "frozen", xSplit: 2, ySplit: 2, topLeftCell: "C3" },
      ];

      // ============================================
      // AUTO FILTER
      // ============================================
      worksheet.autoFilter = {
        from: { row: 2, column: 1 },
        to: { row: 2, column: allColumns.length },
      };

      // ============================================
      // GENERATE AND DOWNLOAD
      // ============================================
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(
        blob,
        `District_Report_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    } catch (error) {
      console.error("Excel export error:", error);
      alert("Failed to export Excel file. Please try again.");
    } finally {
      setExporting(false);
    }
  }, [reportData, allSpeciesList]);
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        height: "calc(100vh - 60px)",
        width: "97.5%",
        display: "flex",
        flexDirection: "column",
        p: 2,
        backgroundColor: "#f4f6f8",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 1,
          alignItems: "center",
        }}
      >
        {/*  Added Back Button next to Search */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {id && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/department-report")}
            >
              Back
            </Button>
          )}
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by District..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "300px",
              backgroundColor: "white",
              borderRadius: "4px",
            }}
          />
        </Box>

        <Button
          variant="contained"
          color="success"
          startIcon={<FileDownloadIcon />}
          onClick={exportToExcel}
          disabled={exporting || loading}
          sx={{
            minWidth: 180,
            gap: 1,
          }}
        >
          {exporting ? "Exporting..." : "Download Excel"}
        </Button>
      </Box>

      <Box
        sx={{ flexGrow: 1, bgcolor: "white", borderRadius: 1, boxShadow: 3 }}
      >
        <DataGrid
          apiRef={apiRef}
          rows={reportData}
          columns={columns}
          columnGroupingModel={columnGroupingModel}
          loading={loading}
          density="compact"
          rowHeight={38}
          disableRowSelectionOnClick
          filterModel={{
            items: [
              {
                field: "district_name",
                operator: "contains",
                value: searchText,
              },
            ],
          }}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              whiteSpace: "nowrap",
            },
            "& .saplings-group-header .MuiDataGrid-columnHeaderTitleContainer":
              {
                backgroundColor: "#f1c27d",
                justifyContent: "center",
              },
            "& .summary-group-header .MuiDataGrid-columnHeaderTitleContainer": {
              backgroundColor: "#d9ead3",
              justifyContent: "center",
            },
          }}
          slots={{ toolbar: GridToolbar }}
          pinnedColumns={{
            left: ["district_name"],
            right: [
              "species_count",
              "total_target",
              "total_stock_saplings",
              "total_selled",
              "total_selled_price",
            ],
          }}
        />
      </Box>
    </Box>
  );
};

export default DistrictReport;
